/**
 * NIZAM ONE ERP — Pagination Utilities (Batch 12B / PERF-001)
 *
 * Standard pagination types and helpers for all list queries.
 * Prevents unbounded result sets under concurrent load.
 */

export interface PaginationOpts {
  /** Page number (0-indexed). Default: 0 */
  page?: number;
  /** Rows per page. Default: 50. Max: 500 for list views, 5000 for exports */
  pageSize?: number;
}

export interface PaginatedResult<T> {
  data: T[];
  count: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

/** Standard list-view page size */
export const DEFAULT_PAGE_SIZE = 50;
/** Admin / dictionary table page size (smaller, more fields) */
export const ADMIN_PAGE_SIZE = 25;
/** Lightweight dictionary tables (low-field, high-use) */
export const DICT_PAGE_SIZE = 100;
/** Maximum safe page size for export queries */
export const EXPORT_MAX_SIZE = 5000;

/**
 * Resolves pagination parameters to a range (from, to) for Supabase .range().
 * Always enforces a maximum to prevent accidental full-table scans.
 */
export function resolvePagination(opts: PaginationOpts = {}, maxSize = DEFAULT_PAGE_SIZE) {
  const page     = Math.max(0, opts.page ?? 0);
  const pageSize = Math.min(Math.max(1, opts.pageSize ?? maxSize), EXPORT_MAX_SIZE);
  const from     = page * pageSize;
  const to       = from + pageSize - 1;
  return { page, pageSize, from, to };
}

/** Server-cap-safe chunk size: Supabase's PostgREST db-max-rows default. */
export const CHUNK_SIZE = 1000;

/**
 * Fetches an ENTIRE filtered result set in server-cap-safe chunks.
 *
 * PostgREST's db-max-rows (1000 on Supabase) silently caps every response —
 * INCLUDING requests with an explicit .limit() above it — so any "fetch all"
 * read (reports, statements, reconciliations) must loop. This helper uses
 * keyset pagination: `buildChunk` receives the last row's cursor (null on the
 * first call) and must return a query filtered to rows AFTER that cursor,
 * ordered ascending by the cursor column, and limited to `chunkSize` (e.g.
 * `.gt('id', cursor).order('id').limit(chunkSize)`). Keyset keeps every chunk
 * O(chunk) where offset paging degrades linearly, and the loop terminates on
 * an empty page, so a lowered server cap shortens pages without truncating.
 *
 * `maxRows` is a loud backstop: 'throw' (default) fails the whole read rather
 * than silently misreporting; 'truncate' returns the first `maxRows` rows for
 * callers that genuinely want a bounded sample (say so in the UI).
 */
export async function fetchAllChunked<Row>(
  buildChunk: (cursor: string | null, chunkSize: number) => PromiseLike<{ data: Row[] | null; error: { message: string } | null }>,
  opts: { chunkSize?: number; maxRows?: number; onMaxRows?: 'throw' | 'truncate'; getCursor?: (row: Row) => string } = {},
): Promise<Row[]> {
  const chunkSize = opts.chunkSize ?? CHUNK_SIZE;
  const maxRows   = opts.maxRows ?? 200_000;
  const onMax     = opts.onMaxRows ?? 'throw';
  const getCursor = opts.getCursor ?? ((r: Row) => (r as { id: string }).id);
  const all: Row[] = [];
  let cursor: string | null = null;
  for (;;) {
    const { data, error } = await buildChunk(cursor, chunkSize);
    if (error) throw new Error(error.message);
    if (!data || data.length === 0) break;
    all.push(...data);
    if (all.length >= maxRows) {
      if (onMax === 'throw') {
        throw new Error(`fetchAllChunked: result exceeds maxRows=${maxRows} — narrow the filters or raise the bound deliberately`);
      }
      return all.slice(0, maxRows);
    }
    cursor = getCursor(data[data.length - 1]);
  }
  return all;
}

/** Page size for keyset-paged finance RPCs. Must stay at or below the bound the
 *  functions themselves enforce (1..500), which is well under PostgREST's
 *  1000-row response ceiling so a full page can never be the truncated one. */
export const RPC_PAGE_SIZE = 500;

/**
 * Assembles the COMPLETE result of a keyset-paged RPC and proves it is complete.
 *
 * FIN-TB-ROWCAP-1 / FIN-RPC-ROWCAP-2: a single un-ranged `rpc()` is silently
 * capped at 1000 rows — HTTP 200, no error, a short financial statement that
 * looks whole. So every account-scale finance RPC ships as a cursor page plus an
 * authoritative count, and this helper is the only place that reassembles them:
 * it walks the cursor, refuses a repeated key, and fails closed when the
 * assembled row count disagrees with the count the server reported.
 *
 * `fetchPage` receives the cursor of the last row seen (null on the first call).
 * `rowKey` must be unique per row — it is what duplicate detection compares.
 */
export async function fetchAllPagedRpc<Row>(
  fetchCount: () => PromiseLike<{ data: unknown; error: { message: string } | null }>,
  fetchPage: (cursor: Row | null, pageSize: number) => PromiseLike<{ data: Row[] | null; error: { message: string } | null }>,
  rowKey: (row: Row) => string,
  label: string,
): Promise<Row[]> {
  const { data: countData, error: countError } = await fetchCount();
  if (countError) throw new Error(countError.message);
  const expected = Number(countData ?? 0);

  const rows: Row[] = [];
  const seen = new Set<string>();
  let cursor: Row | null = null;

  for (;;) {
    const { data, error } = await fetchPage(cursor, RPC_PAGE_SIZE);
    if (error) throw new Error(error.message);
    const page = data ?? [];
    if (page.length === 0) break;

    for (const row of page) {
      const key = rowKey(row);
      if (seen.has(key)) {
        throw new Error(`${label}: paging returned ${key} twice — refusing to build a report from a broken cursor.`);
      }
      seen.add(key);
      rows.push(row);
    }

    cursor = page[page.length - 1];
    if (page.length < RPC_PAGE_SIZE) break;
  }

  if (rows.length !== expected) {
    throw new Error(`${label} is incomplete: assembled ${rows.length} of ${expected} rows. Refusing to display a partial report.`);
  }
  return rows;
}
